import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getFormValues, initialize } from 'redux-form';
import { groupBy } from 'lodash';
import differenceInDays from 'date-fns/differenceInDays';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { FormName } from '@topconagriculture/feed-types';

import api from '../../../core/api';
import { notificationError } from '../../../core/helpers/utils/notificationUtils';
import {
  composeErrorMessage,
  getDateFormat,
  getDecimalSeparator,
  getThousandSeparator,
  getTimeFormat,
  numericStringComapreFunction,
} from '../../../core/helpers/utils/utils';
import { LoadDeliveryData } from '../../entities/dataTools/dataTools.types';
import { LoadersNames } from '../../ui/ui.constants';
import {
  downloadCloseoutReport,
  downloadCostSummaryReport,
  getLotCattleMovements,
  getCloseoutReport,
  getCostSummaryReport,
  getDeliveries,
  previewLot,
  validateAdditionalCostEndDate,
} from './appLots.actions';
import { DeliveryInLotCreation, MovementInCreation } from './appLots.types';
import { appLotsSelector, settingsSelector } from '../../selectors';
import { Movement } from '../../entities/lots/lots.types';
import { toFarmTimeZone, toLocalDate, toTimezoneDate } from '../../../core/helpers/utils/dateUtils';
import { transfersHistoryLotIsEdit } from './appLots.selector';
import { Forms } from '../../form/form.selector';
import { getSelectedGroup, getSelectedLot } from '../../selected/selected.selector';
import { getCostTypeById } from '../../entities/costTypes/costTypes.selector';
import { ChargeType } from '../../entities/costTypes/costTypes.types';
import { getDaysBack, getRangeType } from '../../../shared/DateRangePicker/utils';
import { successHandler } from '../../../core/helpers/utils/sagaUtils';
import { ReportType } from '../../entities/reports/reports.constants';

const userSettings = {
  dateFormat: getDateFormat(),
  timeFormat: getTimeFormat(),
  decimalSeparator: getDecimalSeparator(),
  thousandSeparator: getThousandSeparator(),
};

function* getDeliveriesSaga(action: ReturnType<typeof getDeliveries.started>) {
  yield put(showLoader(LoadersNames.getDeliveriesInLotCreation));
  try {
    const { date, groupId } = action.payload;
    const dateParam = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59);
    const batchesList: LoadDeliveryData[] = yield call(api.dataTools.getLoadsDeliveriesBatches, 0, dateParam, true);
    const deliveries = batchesList.reduce((acc, curr) => {
      return [...acc, ...curr.deliveries.filter((x) => x.groupId === groupId)];
    }, []);

    const result: DeliveryInLotCreation[] = deliveries.map((x) => ({
      deliveryId: x.id,
      headCount: x.headCount,
      date: new Date(x.deliveryTime),
      weight: x.actualWeight,
    }));

    yield put(
      getDeliveries.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getDeliveriesInLotCreation));
}

function* getCloseoutReportSaga(action: ReturnType<typeof getCloseoutReport.started>) {
  yield put(showLoader(LoadersNames.getCloseoutPreview));
  try {
    const closeouts: MovementInCreation[] = yield select(appLotsSelector.getMovements);
    const includedCloseouts = action.payload.includedCloseouts || [];
    const data = {
      dispatches: !!includedCloseouts.length ? closeouts.filter((x) => includedCloseouts.includes(x.uuid)) : undefined,
      preset: {
        viewSettings: action.payload.includeOptions,
        dataSettings: {
          dispatchIds: action.payload.includedCloseoutsExisting,
          includeDeadAnimals: action.payload.includeOptions.showDeaths,
        },
      },
      userSettings,
    };
    const result = yield call(api.lots.getCloseoutReport, action.payload.lotId, data);

    yield put(
      getCloseoutReport.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getCloseoutPreview));
}

function* downloadCloseoutReportSaga(action: ReturnType<typeof downloadCloseoutReport.started>) {
  yield put(showLoader(LoadersNames.downloadCloseoutReport));
  try {
    const reportOptions = action.payload.previewPayload;
    const closeouts: MovementInCreation[] = yield select(appLotsSelector.getMovements);
    const includedCloseouts = reportOptions.includedCloseouts || [];
    const data = {
      dispatches: !!includedCloseouts.length ? closeouts.filter((x) => includedCloseouts.includes(x.uuid)) : undefined,
      preset: {
        viewSettings: reportOptions.includeOptions,
        dataSettings: {
          dispatchIds: reportOptions.includedCloseoutsExisting,
          includeDeadAnimals: reportOptions.includeOptions.showDeaths,
        },
      },
      userSettings,
    };
    yield call(api.lots.downloadCloseoutReport, reportOptions.lotId, action.payload.fileName, data);

    if (action.meta && typeof action.meta.onSuccess === 'function') {
      action.meta.onSuccess();
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.downloadCloseoutReport));
}

function* previewLotSaga(action: ReturnType<typeof previewLot.started>) {
  yield put(showLoader(LoadersNames.previewLot));
  try {
    const id = action.payload.id;

    const movements = (action.payload.movements || []).map((x) => {
      const { groups, changed, error, timestamp, toGroupId, fromGroupId, ...movement } = x;

      return {
        ...movement,
        timestamp: toLocalDate(new Date(timestamp)),
        toGroupId: toGroupId || null,
        fromGroupId: fromGroupId || null,
      } as Movement;
    });

    const lot = {
      id,
      movements,
    };
    const result = yield call(api.lots.updateLot, id, lot, FormName.lotTransfersHistory, true);
    const isEdit = yield select(transfersHistoryLotIsEdit);

    if (isEdit) {
      yield put(
        previewLot.done({
          params: action.payload,
          result,
        }),
      );
    }
  } catch (err) {
    if (err.status === 409) {
      yield put(
        previewLot.done({
          params: action.payload,
          result: err.data,
        }),
      );
    } else {
      yield put(notificationError(composeErrorMessage(err)));
    }
  }
  yield put(hideLoader(LoadersNames.previewLot));
}

function* validateAdditionalCostEndDateSaga(action: ReturnType<typeof validateAdditionalCostEndDate>) {
  const chargeType = action.payload;
  const timezone = yield select(settingsSelector.getTimeZone);
  const getCostType = yield select(getCostTypeById);

  try {
    const formValues = yield select(getFormValues(Forms.costTypesEditorForm));
    const selectedLot = yield select(getSelectedLot);
    const selectedGroup = yield select(getSelectedGroup);
    let id: string;

    switch (chargeType) {
      case ChargeType.LOT:
        id = selectedLot.id;
        break;
      case ChargeType.GROUP:
        id = selectedGroup.id;
    }

    const additionalCosts = Object.values<any>(formValues)
      .flat()
      .map(({ changed, error, appliedPer, chargePeriod, chargeType, startDateTime, endDateTime, ...rest }) => ({
        ...rest,
        startDateTime: toTimezoneDate(toLocalDate(new Date(startDateTime)), timezone),
        endDateTime: endDateTime ? toTimezoneDate(toLocalDate(new Date(endDateTime)), timezone) : undefined,
      })) as any;

    let result;

    switch (chargeType) {
      case ChargeType.LOT:
        result = yield call(api.lots.updateLot, id, { id, additionalCosts }, FormName.lotEdit, true);
        break;
      case ChargeType.GROUP:
        result = yield call(api.group.patchGroup, { id, additionalCosts }, id, FormName.lotEdit, true);
    }

    const newAdditionalCosts = result.additionalCosts
      ?.map((cost) => ({
        ...cost,
        startDateTime: toFarmTimeZone(new Date(cost.startDateTime), timezone),
        endDateTime: cost.endDateTime ? toFarmTimeZone(new Date(cost.endDateTime), timezone) : undefined,
        costTypeDescription: getCostType(cost.additionalCostTypeId)?.description || '',
      }))
      .sort((cost1, cost2) => numericStringComapreFunction(cost1.costTypeDescription, cost2.costTypeDescription));
    yield put(initialize(Forms.costTypesEditorForm, groupBy(newAdditionalCosts, 'additionalCostTypeId')));
  } catch (err) {
    const additionalCosts = err?.data?.additionalCosts
      ?.map((cost) => ({
        ...cost,
        startDateTime: toFarmTimeZone(new Date(cost.startDateTime), timezone),
        endDateTime: cost.endDateTime ? toFarmTimeZone(new Date(cost.endDateTime), timezone) : undefined,
        costTypeDescription: getCostType(cost.additionalCostTypeId)?.description || '',
      }))
      .sort((cost1, cost2) => numericStringComapreFunction(cost1.costTypeDescription, cost2.costTypeDescription));
    yield put(initialize(Forms.costTypesEditorForm, groupBy(additionalCosts, 'additionalCostTypeId')));
    const message = yield composeErrorMessage(err);
    yield put(notificationError({ title: message.translationKey || message.title, autoDismiss: 5 }));
  }
}

export function* getCostSummaryReportSaga(action: ReturnType<typeof getCostSummaryReport.started>) {
  const { id } = action.payload;
  yield put(showLoader(LoadersNames.fetchCostSummaryReport));
  try {
    const formValue = yield select(getFormValues(Forms.costSummaryReport));
    const timezone = yield select(settingsSelector.getTimeZone);
    const { selectedTimeRange } = formValue;
    const { startDate, endDate, labelKey } = selectedTimeRange;
    const daysBack = getDaysBack(labelKey, differenceInDays(endDate, startDate));

    const dateRangeType = getRangeType(labelKey);

    const preset = {
      preset: {
        dataSettings: {
          dateRangeType,
          daysBack,
          endDate: toTimezoneDate(toLocalDate(endDate), timezone),
        },
      },
      userSettings,
    };

    const result = yield call(api.lots.getCostSummaryReport, id, {
      ...preset,
    } as any);

    yield put(
      getCostSummaryReport.done({
        result,
        params: action.payload,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchCostSummaryReport));
}

export function* downloadCostSummaryReportSaga(action: ReturnType<typeof downloadCostSummaryReport>) {
  const { fileName, format, lotId } = action.payload;
  try {
    const formValue = yield select(getFormValues(Forms.costSummaryReport));
    const timezone = yield select(settingsSelector.getTimeZone);
    const { selectedTimeRange } = formValue;
    const { startDate, endDate, labelKey } = selectedTimeRange;
    const daysBack = getDaysBack(labelKey, differenceInDays(endDate, startDate));

    const dateRangeType = getRangeType(labelKey);

    const preset = {
      dataSettings: {
        dateRangeType,
        daysBack,
        endDate: toTimezoneDate(toLocalDate(endDate), timezone),
      },
      userSettings,
    } as any;

    yield call(
      api.lots.downloadCostSummaryReport,
      `application/${format.type}`,
      format.ext,
      preset,
      fileName || '',
      lotId,
    );
    successHandler(action.meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* getLotCattleMovementsSaga(action: ReturnType<typeof getLotCattleMovements.started>) {
  yield put(showLoader(LoadersNames.fetchReport));
  try {
    const preset = {
      dataSettings: {
        ...action.payload,
        daysBack: 0,
      },
      viewSettings: {
        pageOrientation: 0,
      },
    };
    const result = yield call(api.reports.getReportDataByPreset, ReportType.CattleMovements, preset);
    yield put(
      getLotCattleMovements.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchReport));
}

export function* watchAppLotsSaga() {
  yield takeLatest(getDeliveries.started, getDeliveriesSaga);
  yield takeLatest(getCloseoutReport.started, getCloseoutReportSaga);
  yield takeLatest(downloadCloseoutReport.started, downloadCloseoutReportSaga);
  yield takeLatest(previewLot.started, previewLotSaga);
  yield takeLatest(validateAdditionalCostEndDate.type, validateAdditionalCostEndDateSaga);
  yield takeLatest(getCostSummaryReport.started, getCostSummaryReportSaga);
  yield takeLatest(downloadCostSummaryReport.type, downloadCostSummaryReportSaga);
  yield takeLatest(getLotCattleMovements.started, getLotCattleMovementsSaga);
}
